import { create } from 'zustand';
import { dashboardConfigApi } from '../services/api';
import type { DashboardConfig, DashboardTile } from '../types';

interface DashboardConfigState {
  config: DashboardConfig | null;
  loading: boolean;

  fetchConfig: () => Promise<void>;
  updateConfig: (updates: Partial<DashboardConfig>) => Promise<void>;
  updateTiles: (tiles: DashboardTile[]) => Promise<void>;
  updateQuickAccess: (projectIds: string[]) => Promise<void>;
  setSelectedOrg: (orgId: string) => Promise<void>;
  /** Note that a project was opened. Safe to call on every navigation. */
  recordProjectAccess: (projectId: string) => void;
}

/** Last project we told the server about, so a repeat navigation to the same
 *  project makes no request at all. The server debounces too; this avoids even
 *  the round trip. */
let lastRecorded = '';

export const useDashboardConfigStore = create<DashboardConfigState>((set) => ({
  config: null,
  loading: false,

  fetchConfig: async () => {
    set({ loading: true });
    try {
      const config = await dashboardConfigApi.get();
      set({ config, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  recordProjectAccess: (projectId) => {
    if (!projectId || projectId === lastRecorded) return;
    lastRecorded = projectId;
    // Fire and forget: bookkeeping must never delay or block navigation, and a
    // failure here is not worth surfacing to the user.
    dashboardConfigApi
      .recordRecentProject(projectId)
      .then(({ lastProjectId, recentProjectIds }) =>
        set((s) => (s.config
          ? { config: { ...s.config, lastProjectId, recentProjectIds } }
          : s)))
      .catch(() => { lastRecorded = ''; });   // allow a retry on the next open
  },

  updateConfig: async (updates) => {
    try {
      const config = await dashboardConfigApi.update(updates);
      set({ config });
    } catch {
      // Silently fail
    }
  },

  updateTiles: async (tiles) => {
    try {
      const config = await dashboardConfigApi.update({ tiles });
      set({ config });
    } catch {
      // Silently fail
    }
  },

  updateQuickAccess: async (projectIds) => {
    // Optimistic update
    set((state) => ({
      config: state.config ? { ...state.config, quickAccessProjectIds: projectIds } : state.config,
    }));
    try {
      const config = await dashboardConfigApi.update({ quickAccessProjectIds: projectIds });
      set({ config });
    } catch (err) {
      console.error('Failed to update quick access:', err);
      // Revert on failure
      set((state) => ({
        config: state.config ? { ...state.config, quickAccessProjectIds: state.config.quickAccessProjectIds } : state.config,
      }));
    }
  },

  setSelectedOrg: async (orgId) => {
    try {
      const config = await dashboardConfigApi.update({ organizationId: orgId });
      set({ config });
    } catch {
      // Silently fail
    }
  },
}));
